import { z } from 'zod';
import { LANGUAGES } from './language.js';
import { THEME_MODES } from './theme.js';
import { ROLES } from './roles.js';

/**
 * The public shape of a user as returned by the API. This never contains
 * secrets — it is safe to send to the client.
 */
export interface UserDTO {
  id: string;
  telegramId: string;
  firstName: string;
  lastName: string | null;
  username: string | null;
  photoUrl: string | null;
  role: (typeof ROLES)[number];
  language: (typeof LANGUAGES)[number];
  themeMode: (typeof THEME_MODES)[number];
  themeId: string;
  notificationsEnabled: boolean;
  /** Whether the user has completed the first-launch onboarding. */
  onboarded: boolean;
  /** Effective Premium flag (true only while a permanent grant or unexpired subscription is active). */
  isPremium: boolean;
  /** ISO expiry of a time-limited Premium subscription, else null (FREE or permanent). */
  premiumExpiresAt: string | null;
  /** Purchased duration tier; null on legacy records (treated as PREMIUM_PLUS). */
  premiumTier: 'PREMIUM' | 'PREMIUM_PLUS' | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * User-editable preference fields. Used by the "update me" endpoint.
 * All fields optional — only provided keys are updated.
 */
export const updateProfileSchema = z
  .object({
    language: z.enum(LANGUAGES).optional(),
    themeMode: z.enum(THEME_MODES).optional(),
    themeId: z.string().min(1).max(64).optional(),
    notificationsEnabled: z.boolean().optional(),
    onboarded: z.boolean().optional(),
  })
  .strict();

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
